import './css/app.css';

import heroWithCta from '~/js/components/hero-with-cta';
import productThumb from '~/js/components/product-thumb';
import colorSelect from '~/js/components/color-select';
import addToCart from '~/js/components/add-to-cart';
import uspHeader from '~/js/components/usp-header';
import header from '~/js/components/header';
import megaMenu from '~/js/components/mega-menu';
import mobileMenu from '~/js/components/mobile-menu';
import tooltip from '~/js/components/tooltip';
import collectionTemplate from '~/js/components/collection-template';
import faqAccordion from '~/js/components/faq-accordion';
import accordion from '~/js/components/accordion';
import roomleConfigurator from '~/js/components/roomle-configurator';
import contactForm from '~/js/components/contact-form';
import heroFullWidthVideo from '~/js/components/hero-full-width-video';
import price from '~/js/components/price';
import stockInfo from '~/js/components/stock-info';
import emblaSlider from '~/js/components/embla-slider';
import collectionList from '~/js/components/collection-list';
import collectionAllTemplate from '~/js/components/collection-all-template';
import collectionFilters from '~/js/components/collection-filters';
import customSelect from '~/js/components/select';
import hotspots from '~/js/components/hotspots';
import cartDrawer from '~/js/components/cart-drawer';
import cartProductFlags from '~/js/components/cart-product-flags';
import lighting from '~/js/components/lighting';
import stockErrorModal from '~/js/components/stock-error-modal';
import comingCollections from '~/js/components/coming-collections';
import notifyModal from '~/js/components/notify-modal';
import swymWishlistButton from '~/js/components/swym-wishlist-button';
import swymWishlistPage from '~/js/components/swym-wishlist-page';
import wallpaperCalculator from '~/js/components/wallpaper-calculator';
import mainCartSell from '~/js/components/main-cart-sell';
import cartToast from '~/js/components/cart-toast';
import productThumbCarousel from '~/js/components/product-thumb-carousel';
import categoryOverview from '~/js/components/category-overview';
import { initConsentLinks } from '~/js/lib/consent';

const componentsMap = {
  mainCartSell,
  accordion,
  heroWithCta,
  productThumb,
  colorSelect,
  addToCart,
  uspHeader,
  header,
  megaMenu,
  mobileMenu,
  tooltip,
  collectionTemplate,
  faqAccordion,
  roomleConfigurator,
  contactForm,
  heroFullWidthVideo,
  price,
  stockInfo,
  emblaSlider,
  collectionList,
  collectionAllTemplate,
  collectionFilters,
  customSelect,
  hotspots,
  cartDrawer,
  cartProductFlags,
  lighting,
  stockErrorModal,
  comingCollections,
  notifyModal,
  swymWishlistButton,
  swymWishlistPage,
  wallpaperCalculator,
  cartToast,
  productThumbCarousel,
  categoryOverview,
};

// A node mounts at most once. Re-mounting stacks duplicate document-level listeners that
// nothing removes.
const mountedNodes = new WeakSet();

const mountComponent = (el) => {
  const mount = componentsMap[el.dataset.component];

  if (!mount || mountedNodes.has(el)) return;

  // Marked before the call so a mount that throws part-way is not retried onto a node it
  // has already partly wired. The catch keeps the throw local — callers sweep in a
  // forEach, which an escaping exception would abandon.
  mountedNodes.add(el);

  try {
    mount(el);
  } catch (error) {
    console.error(
      `[app] "${el.dataset.component}" failed to mount; skipping it.`,
      { node: el },
      error
    );
  }
};

// Scoped to `event.target` when a section reload supplies one; the guard, not the scoping,
// is what prevents duplicates.
const mountComponents = (event) => {
  const root = event?.target instanceof Element ? event.target : document;

  if (root !== document && root.matches('[data-component]')) {
    mountComponent(root);
  }

  root.querySelectorAll('[data-component]').forEach(mountComponent);
};

const loadAddToCartComponents = (event) => {
  const container = event.detail?.container;

  if (!(container instanceof Element)) {
    console.warn(
      '[app] shopify:section:load:addToCart dispatched without an element in detail.container; add-to-cart buttons not mounted.',
      event
    );
    return;
  }

  container
    .querySelectorAll('[data-component="addToCart"]')
    .forEach(mountComponent);
};

const loadEmblaSliderComponents = (event) => {
  const container = event.detail?.container;

  if (!(container instanceof Element)) {
    console.warn(
      '[app] shopify:section:load:emblaSlider dispatched without an element in detail.container; slider not re-initialised.',
      event
    );
    return;
  }

  // A re-init hook, not a mount: the slides are new but the container persists, so routing
  // this through mountComponent would suppress the call this path exists to make. Record
  // it anyway so a later sweep does not stack a second instance on the same viewport.
  mountedNodes.add(container);

  try {
    emblaSlider(container);
  } catch (error) {
    console.error(
      '[app] emblaSlider failed to re-initialise.',
      { node: container },
      error
    );
  }
};

const initVideoPauseButtons = () => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-video-pause]');
    if (!btn) return;

    const container = btn.parentElement;
    const videos = container.querySelectorAll('video[data-autoplay-video]');
    if (!videos.length) return;

    const pauseIcon = btn.querySelector('[data-pause-icon]');
    const playIcon = btn.querySelector('[data-play-icon]');
    const isPlaying = [...videos].some((v) => !v.paused);

    videos.forEach((video) => {
      if (isPlaying) {
        video.pause();
      } else {
        const playback = video.play();
        if (playback) playback.catch(() => {});
      }
    });

    if (pauseIcon) pauseIcon.classList.toggle('hidden', isPlaying);
    if (playIcon) playIcon.classList.toggle('hidden', !isPlaying);
    btn.classList.toggle('!opacity-100', !isPlaying);
    btn.setAttribute(
      'aria-label',
      isPlaying
        ? btn.dataset.playLabel || 'Play video'
        : btn.dataset.pauseLabel || 'Pause video'
    );
  });
};

document.addEventListener('DOMContentLoaded', mountComponents);
document.addEventListener('DOMContentLoaded', initVideoPauseButtons);
document.addEventListener('DOMContentLoaded', initConsentLinks);
document.addEventListener('shopify:section:load', mountComponents);

document.addEventListener('shopify:section:load:addToCart', loadAddToCartComponents);
document.addEventListener('shopify:section:load:emblaSlider', loadEmblaSliderComponents);
